import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useLang } from "../LangContext";

// Web3Forms endpoint — receives the form and emails it straight to the address
// associated with the access key (configured in Web3Forms dashboard).
// Get a free key (250 msg/month) at https://web3forms.com/
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export const ContactDialog = ({ open, onOpenChange }) => {
  const { t } = useLang();
  const c = t.contact;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  // Honeypot — bots tend to fill every visible field. We hide this one.
  const [botField, setBotField] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => {
    setName("");
    setEmail("");
    setSubject("");
    setMessage("");
    setBotField("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(c.errors.name);
    if (!isValidEmail(email)) return toast.error(c.errors.email);
    if (message.trim().length < 5) return toast.error(c.errors.message);
    if (botField) return; // silently drop bot submissions

    if (!WEB3FORMS_KEY) {
      toast.error(c.errors.notConfigured);
      return;
    }

    setSending(true);
    try {
      const { data } = await axios.post(WEB3FORMS_ENDPOINT, {
        access_key: WEB3FORMS_KEY,
        from_name: "Site Bac à jeux",
        subject: subject.trim() || `Nouveau message — ${name.trim()}`,
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        // helpful metadata
        replyto: email.trim(),
        page: "bacajeux.com",
      });
      if (data && data.success) {
        toast.success(c.success);
        reset();
        onOpenChange(false);
      } else {
        toast.error(data?.message || c.errors.generic);
      }
    } catch (err) {
      const msg = err?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : c.errors.generic);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 border-white/15 bg-[#0c1120] text-white overflow-hidden"
        data-testid="contact-dialog"
      >
        {/* Top gradient bar */}
        <div className="h-1.5 bg-baj-grad" />

        <DialogHeader className="px-6 pt-6 pb-2 text-left">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-baj-grad flex items-center justify-center shadow-lg shadow-fuchsia-500/30">
              <Mail className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="font-display text-2xl font-extrabold text-white">
                {c.title}
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-sm mt-0.5">
                {c.sub}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form
          onSubmit={submit}
          className="px-6 pb-6 pt-3 space-y-3"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField
              label={c.fields.name}
              value={name}
              onChange={setName}
              placeholder={c.fields.namePlaceholder}
              testid="contact-input-name"
              required
            />
            <FormField
              label={c.fields.email}
              type="text"
              value={email}
              onChange={setEmail}
              placeholder={c.fields.emailPlaceholder}
              testid="contact-input-email"
              required
            />
          </div>
          <FormField
            label={c.fields.subject}
            value={subject}
            onChange={setSubject}
            placeholder={c.fields.subjectPlaceholder}
            testid="contact-input-subject"
          />
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-slate-300 mb-2">
              {c.fields.message}
              <span className="text-fuchsia-400 ml-1">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={c.fields.messagePlaceholder}
              rows={5}
              className="w-full bg-white/5 border border-white/15 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-fuchsia-400/60 focus:bg-white/10 transition resize-none"
              data-testid="contact-input-message"
            />
            <div className="mt-1 text-[10px] text-slate-500 text-right">
              {message.trim().length}/4000
            </div>
          </div>

          {/* Honeypot — hidden from humans, visible to bots */}
          <input
            type="text"
            tabIndex="-1"
            autoComplete="off"
            value={botField}
            onChange={(e) => setBotField(e.target.value)}
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              opacity: 0,
            }}
            aria-hidden="true"
          />

          <div className="flex items-center justify-between gap-3 pt-1">
            <p className="text-[11px] text-slate-500 leading-relaxed">
              {c.privacy}
            </p>
            <button
              type="submit"
              disabled={sending}
              className="btn-primary py-2.5 px-5 text-sm shrink-0 disabled:opacity-60 disabled:cursor-wait"
              data-testid="contact-submit-button"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sending ? c.sending : c.submit}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FormField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  testid,
  required,
}) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] font-bold text-slate-300 mb-2">
      {label}
      {required && <span className="text-fuchsia-400 ml-1">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white/5 border border-white/15 rounded-full px-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-fuchsia-400/60 focus:bg-white/10 transition"
      data-testid={testid}
    />
  </div>
);
